/**
 * Input and result of `priceOrder()` (03-modules.md §6). Shapes follow the
 * pricing, menu, org and orders tables in 04-data-model.md: the server (or an
 * app, for a preview) loads the rows and resolves menu prices, then passes
 * plain values here. Nothing in this package reads a clock, a database or the
 * network.
 *
 * Money is `Paise` (integer bigint), rates are basis points, quantities are
 * integer milli-units (1000 = 1, matching `numeric(10,3)`).
 */
import type { BasisPoints, Paise } from '@qrkey/contracts/money';
import type { PriceContext } from '@qrkey/contracts/org';

export type OrderType = 'dine_in' | 'takeaway' | 'delivery' | 'aggregator';

/** Intra-state supply pays CGST + SGST; inter-state pays IGST. Cess applies to both. */
export type SupplyType = 'intra_state' | 'inter_state';

export type TaxComponent = 'cgst' | 'sgst' | 'igst' | 'cess';

export const TAX_COMPONENTS: readonly TaxComponent[] = ['cgst', 'sgst', 'igst', 'cess'];

/** `tax_rule`: one component of a tax group, valid for `[effectiveFrom, effectiveTo)`. */
export interface TaxRule {
  component: TaxComponent;
  rateBp: BasisPoints;
  /** `YYYY-MM-DD`, inclusive. */
  effectiveFrom: string;
  /** `YYYY-MM-DD`, exclusive (the day the next rule starts); `null` = open-ended. */
  effectiveTo: string | null;
}

/** `tax_group` with its rules (all of them; the calculator picks those effective on `taxDate`). */
export interface TaxGroup {
  id: string;
  name?: string;
  rules: readonly TaxRule[];
}

/** An add-on, option or variant surcharge on a line (`order_line_modifier`). */
export interface ModifierInput {
  kind?: string;
  refId?: string;
  name?: string;
  /** Price of one unit of this modifier, as resolved by menu for the order's context. */
  unitPricePaise: Paise;
  /** Quantity per one unit of the parent line, in milli-units. Default 1000. */
  qtyMilli?: number;
  /** Defaults to the parent line's tax group. */
  taxGroupId?: string | null;
  /** Defaults to the parent line's flag. */
  taxInclusive?: boolean;
}

export interface LineInput {
  /** Unique within the order (the order_line id). Also the final tie-break in apportionment. */
  id: string;
  productId?: string;
  variantId?: string | null;
  categoryId?: string | null;
  name?: string;
  /** Integer milli-units, > 0. */
  qtyMilli: number;
  /** Unit price resolved by menu (section/timing/context price), before tax if exclusive. */
  unitPricePaise: Paise;
  /** `price.tax_inclusive`: the unit price already contains the line's taxes. */
  taxInclusive: boolean;
  /** `product.tax_group_id`; `null` = no tax (exempt / nil-rated). */
  taxGroupId: string | null;
  modifiers?: readonly ModifierInput[];
  /** Complimentary line (`markComplimentary`): shown at its value, charged at zero. */
  complimentary?: boolean;
  /** `false` keeps the line out of every discount (e.g. MRP items). Default true. */
  discountable?: boolean;
}

export type DiscountTarget =
  | { scope: 'order' }
  | { scope: 'item'; lineIds: readonly string[] }
  | { scope: 'category'; categoryIds: readonly string[] };

interface DiscountBase {
  /** `order_discount.id` (or `discount.id`); unique within the input. */
  id: string;
  discountId?: string;
  name?: string;
  target: DiscountTarget;
}

export type DiscountInput =
  | (DiscountBase & {
      calc: 'percent';
      rateBp: BasisPoints;
      /** `discount.max_paise`: cap on this discount's amount. */
      maxPaise?: Paise | null;
    })
  | (DiscountBase & { calc: 'flat'; amountPaise: Paise });

export type ChargeKind = 'packaging' | 'service' | 'delivery' | 'other';

/** Where a charge applies. An omitted or empty list means "any". */
export interface ChargeApplicability {
  orderTypes?: readonly OrderType[];
  /** `other_charge.applies_to` (org): the pricing channels the charge applies to. */
  channels?: readonly PriceContext[];
  /**
   * `order.source` values (see `PricingContext.source`). org's `other_charge`
   * has no source filter today, so charges loaded from org leave this empty.
   */
  sources?: readonly string[];
  sectionIds?: readonly string[];
}

interface ChargeBase {
  /** `other_charge.id`. */
  id: string;
  name?: string;
  kind: ChargeKind;
  appliesTo?: ChargeApplicability;
  /** Taxable charges are taxed (exclusive, on top) with `taxGroupId`'s rules. */
  taxable: boolean;
  taxGroupId?: string | null;
}

export type ChargeInput =
  | (ChargeBase & { calc: 'percent'; rateBp: BasisPoints })
  | (ChargeBase & { calc: 'flat'; amountPaise: Paise });

export interface PricingContext {
  orderType: OrderType;
  /** `order.channel`, the pricing context (`PRICE_CONTEXTS` in contracts); validated. */
  channel: PriceContext;
  /**
   * `order.source` (04-data-model.md): the text tag of the app or integration
   * the order came in through (pos, captain, qr, app, web, phone, swiggy,
   * zomato, api). It is NOT org's `order_source` master row (a uuid with a
   * free name); pass the tag, never that id. Only matched against
   * `ChargeApplicability.sources` and written to the trace. See
   * 11-open-questions.md Q17.
   */
  source: string;
  sectionId?: string | null;
  /**
   * `YYYY-MM-DD`: the date whose tax rules apply (the calendar date of the
   * time of supply in the outlet's timezone). Passed in; never read from a clock.
   */
  taxDate: string;
  supply: SupplyType;
  /** Whole order complimentary (`order.is_complimentary`): every line and charge is zero. */
  complimentary?: boolean;
}

export interface PricingInput {
  context: PricingContext;
  lines: readonly LineInput[];
  taxGroups: readonly TaxGroup[];
  /** Discounts applied to this order, in the order they were applied. */
  discounts?: readonly DiscountInput[];
  /** The outlet's charge rules; the calculator keeps those whose `appliesTo` matches. */
  charges?: readonly ChargeInput[];
  /** Outlet setting `round_off`. */
  roundOff: 'none' | 'nearest_rupee' | 'up_rupee' | 'down_rupee';
}

// ---------------------------------------------------------------------------
// Result

export interface TaxAmounts {
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  cessPaise: Paise;
  /** Sum of the four components. */
  taxPaise: Paise;
}

export interface AppliedRate {
  component: TaxComponent;
  rateBp: BasisPoints;
}

/** A priced part of a line: the item itself or one modifier. */
export interface ComponentResult extends TaxAmounts {
  /** `'item'` or `'modifier:<index>'`. */
  part: string;
  taxGroupId: string | null;
  taxInclusive: boolean;
  rates: readonly AppliedRate[];
  /** Price-basis value before discount (tax-inclusive if `taxInclusive`). */
  grossPaise: Paise;
  /** Price-basis discount share. */
  discountPaise: Paise;
  /** `grossPaise - discountPaise`. */
  netPaise: Paise;
  /** Pre-tax value of `grossPaise`. Equals `grossPaise` for exclusive parts. */
  preTaxGrossPaise: Paise;
  /** `preTaxGrossPaise - taxablePaise`: the discount in pre-tax terms. */
  preTaxDiscountPaise: Paise;
  /** Taxable value after discount. */
  taxablePaise: Paise;
  /** `taxablePaise + taxPaise`. */
  totalPaise: Paise;
}

/**
 * One line's figures. `grossPaise` / `discountPaise` / `netPaise` are on the
 * price basis (tax-inclusive for inclusive items: what the guest sees);
 * `preTax…` and `taxablePaise` are before tax. The `order_line` columns take
 * the pre-tax figures (`discount_paise = preTaxDiscountPaise`), so the lines
 * add up to the order's `Totals`; see README "Storing a result".
 */
export interface LineResult extends TaxAmounts {
  lineId: string;
  complimentary: boolean;
  /** Value of a complimentary line at menu price (price basis); zero otherwise. */
  complimentaryPaise: Paise;
  /** Price basis, before discount. */
  grossPaise: Paise;
  /** Price basis: the line's share of the discounts as applied. Not `order_line.discount_paise`. */
  discountPaise: Paise;
  /** Price basis: `grossPaise - discountPaise` (zero for a complimentary line). */
  netPaise: Paise;
  /** Pre-tax value of `grossPaise`. */
  preTaxGrossPaise: Paise;
  /** Pre-tax discount: `preTaxGrossPaise - taxablePaise`. Fills `order_line.discount_paise`. */
  preTaxDiscountPaise: Paise;
  taxablePaise: Paise;
  totalPaise: Paise;
  /** This line's share of each discount (price basis). */
  discounts: readonly { id: string; amountPaise: Paise }[];
  components: readonly ComponentResult[];
}

export interface DiscountResult {
  id: string;
  discountId: string | null;
  calc: 'percent' | 'flat';
  /** Discountable value the discount was computed on (price basis, after earlier discounts). */
  basePaise: Paise;
  /** The amount before the cap and the base limit. */
  requestedPaise: Paise;
  /** Applied amount (price basis); equals the sum of its line shares. */
  amountPaise: Paise;
  capped: boolean;
  shares: readonly { lineId: string; amountPaise: Paise }[];
}

export interface ChargeResult extends TaxAmounts {
  id: string;
  kind: ChargeKind;
  calc: 'percent' | 'flat';
  taxable: boolean;
  taxGroupId: string | null;
  rates: readonly AppliedRate[];
  /** Pre-tax value the percentage was taken of (zero for flat charges). */
  basePaise: Paise;
  /** Charge amount before tax. */
  amountPaise: Paise;
  /** `amountPaise + taxPaise`. */
  totalPaise: Paise;
}

/** Tax by component and rate, for the invoice and `daily_tax_agg`. */
export interface TaxSummaryRow {
  component: TaxComponent;
  rateBp: BasisPoints;
  taxablePaise: Paise;
  taxPaise: Paise;
}

/**
 * Order totals. Every figure here is pre-tax except `discountAppliedPaise`
 * and `complimentaryPaise`; the field names say so where a line field of the
 * same idea is on the price basis.
 */
export interface Totals extends TaxAmounts {
  /** Sum of line `preTaxGrossPaise` (complimentary lines excluded). Fills `order.subtotal_paise`. */
  subtotalPaise: Paise;
  /** Sum of line `preTaxDiscountPaise`. Fills `order.discount_paise`. */
  preTaxDiscountPaise: Paise;
  /** Sum of discounts as applied (price basis); what the guest sees as "discount". */
  discountAppliedPaise: Paise;
  /** Sum of charge amounts before tax. */
  chargesPaise: Paise;
  /** Value of complimentary lines at menu price (not part of the total). */
  complimentaryPaise: Paise;
  /** Sum of line and charge taxable values (includes non-taxable charges at 0 tax). */
  taxablePaise: Paise;
  /** Before round-off: subtotal - preTaxDiscount + charges + tax. */
  preRoundPaise: Paise;
  roundOffPaise: Paise;
  /** `subtotal - preTaxDiscount + charges + tax + roundOff`. */
  totalPaise: Paise;
}

export interface PricingResult {
  /** `PRICING_VERSION`; stored as `order.pricing_version`. */
  version: string;
  lines: readonly LineResult[];
  discounts: readonly DiscountResult[];
  charges: readonly ChargeResult[];
  taxes: readonly TaxSummaryRow[];
  totals: Totals;
  /** Non-fatal findings (e.g. a tax group with no rule effective on the tax date). */
  warnings: readonly string[];
  /** Human-readable steps for the audit snapshot (`order.price_snapshot`). */
  trace: readonly string[];
}
